// ////with search reset button
import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner, Input, Row } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios from "axios";
import { baseUrl } from "../../../Utils/FileUpload";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FaFilePdf, FaFileExcel } from "react-icons/fa";
import { exportToPDF } from "../../../Utils/pdfExportUtils";
import { excelExportUtils } from "../../../Utils/excelExportUtils";
interface Agent {
  id: string;
  agent_name: string;
  phone: number;
  total_amount: string;
  total_call_duration: string;
  currency: string;
  total_unique_customers: number;
  total_calls: number;
  email: string;
  total_amount_without_currency: number;
}

export default function AgentReport() {
  const [filterText, setFilterText] = useState("");
  const [filteredItems, setFilteredItems] = useState<Agent[]>([]);
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const [totalRows, setTotalRows] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [reportMonth, setReportMonth] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currency, setCurrency] = useState<string>("");

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(filterText);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [filterText]);

  const fetchData = async (page = 1, perPage = 10, sortField = sortColumn, sortDir = sortOrder, search = filterText) => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseUrl}agent/get_all_report`, {
        params: {
          page,
          per_page: perPage,
          search: debouncedSearchTerm.trim() !== "" ? debouncedSearchTerm : null,
          sort_by: sortField,
          sort_order: sortDir,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data: Agent[] = response.data.data;
      setFilteredItems(data);
      setTotalRows(response.data.total);
      setCurrentPage(response.data.current_page);
      setReportMonth(response.data.report_month);
      setCurrency(response.data.currency || "");
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage, perPage);
  }, [currentPage, perPage, debouncedSearchTerm]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchData(page, perPage, sortColumn, sortOrder);
  };

  const handlePerRowsChange = async (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    fetchData(1, newPerPage, sortColumn, sortOrder);
  };

  const handleSort = (column: any, sortDirection: string) => {
    const sortField = column.sortField || "created_at";

    setSortColumn(sortField);
    setSortOrder(sortDirection);
    setCurrentPage(1);
    fetchData(1, perPage, sortField, sortDirection);
  };

  const fetchAllAgentDataWithoutPagination = async (
    searchText = ""
  ): Promise<Agent[]> => {
    try {
      const response = await axios.get(
        `${baseUrl}agent/get_all_no_pagination`,
        {
          params: {
            search: debouncedSearchTerm.trim() !== "" ? debouncedSearchTerm : undefined,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // console.log(response.data.data, "search");

      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching all agent data without pagination:", error);
      return []; // Return empty array on error
    }
  };

  const handleFullReset = () => {
    setFilterText("");
    setDebouncedSearchTerm("");
    setCurrentPage(1);
  };

  const formatAmount = (value: number | string | undefined) => {
    const num = Number(value) || 0;
    return Number.isInteger(num) ? num : num.toFixed(2);
  };

  const agentTableColumns = [
    {
      name: <div style={{ whiteSpace: "normal" }}>S.No</div>,
      cell: (row: Agent, index: number) =>
        (currentPage - 1) * perPage + index + 1,
      width: "80px",
    },
    {
      name: <div style={{ whiteSpace: "normal" }}>Agent Name</div>,
      selector: (row: Agent) => row.agent_name || "N/A",
      sortable: true,
      sortField: "agent_name",
    },
    {
      name: <div style={{ whiteSpace: "normal" }}>Contact Number</div>,
      selector: (row: Agent) => row.phone || "N/A",
      sortable: true,
      sortField: "phone",
    },
    {
      name: <div style={{ whiteSpace: "normal" }}>Total Call Duration (Mins)</div>,
      selector: (row: Agent) => `${row.total_call_duration}`,
      sortable: true,
      sortField: "total_call_duration",
    },
    {
      name: <div style={{ whiteSpace: "normal" }}>Total Customer</div>,
      selector: (row: Agent) =>
        `${row.total_unique_customers}( ${row.total_calls})`,
      sortable: true,
      sortField: "total_unique_customers",
    },
    {
      name: (
        <div style={{ whiteSpace: "normal" }}>
          Total Amount {currency ? `(${currency})` : ""}
        </div>
      ),
      sortable: true,
      sortField: "total_amount",
      cell: (row: Agent) => row.total_amount_without_currency ?? 0,
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#308e87",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #308e87",
      },
    },
    rows: {
      style: {
        borderBottom: "1px solid #dee2e6",
      },
    },
    table: {
      style: {
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      },
    },
  };


  const exportPDF = async () => {
    const allAgentData = await fetchAllAgentDataWithoutPagination(debouncedSearchTerm);
    const tableData = allAgentData.map((row, index) => [
      index + 1,
      row.agent_name || "N/A",
      row.phone || "N/A",
      row.total_call_duration || "0",
      `${row.total_unique_customers || 0} (${row.total_calls || 0})`,
      `${row.total_amount_without_currency || "0"}`,
    ]);

    const currencyMap: Record<string, string> = {
      "₹": "Rs.",
      "€": "EUR",
      "£": "GBP",
      "¥": "YEN",
      "₽": "RUB",
      "₩": "KRW",
      "฿": "THB",
      "₪": "ILS",
      "₫": "VND",
      "₱": "PHP",
    };
    const pdfCurrency = currencyMap[currency] || currency;

    exportToPDF({
      fileName: "Agent_Report",
      columns: [
        "S.No",
        "Agent Name",
        "Contact Number",
        "Duration",
        "Customers (Calls)",
        `Total ${pdfCurrency ? `(${pdfCurrency})` : ""}`,
      ],
      data: tableData,
      emptyDataMessage: "No agent data available for export.",
      autoTableOptions: {
        headStyles: { fillColor: [0, 105, 92] },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
          0: { halign: "center" },
          5: { halign: "right" },
        },
      },
    });
  };

  const exportExcel = async () => {
    const allAgentData = await fetchAllAgentDataWithoutPagination(debouncedSearchTerm);

    if (!allAgentData.length) {
      toast.warn("No agent data available for export.", {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      return;
    }

    const currencyMap: Record<string, string> = {
      "₹": "Rs.",
      "€": "EUR",
      "£": "GBP",
      "¥": "YEN",
      "₽": "RUB",
      "₩": "KRW",
      "฿": "THB",
      "₪": "ILS",
      "₫": "VND",
      "₱": "PHP",
    };
    const excelCurrency = currencyMap[currency] || currency;
    const totalAmountHeader = `Total Amount ${excelCurrency ? `(${excelCurrency})` : ""}`;

    const tableData = allAgentData.map((row, index) => ({
      "S.No": index + 1,
      "Agent Name": row.agent_name || "N/A",
      "Contact Number": row.phone || "N/A",
      "Total Call Duration": row.total_call_duration || "0",
      "Total Customer (Calls)": `${row.total_unique_customers || 0} (${row.total_calls || 0
        })`,
      [totalAmountHeader]: row.total_amount_without_currency || "0",
    }));

    excelExportUtils(tableData, "Agent_Report", "Agents Summary");
  };

  return (
    <>
      <div className="page-body">
        <Breadcrumbs
          // pageTitle={`Agent Report${reportMonth ? ` (${reportMonth})` : ""}`}
          pageTitle={`Agent Report`}


          parent="Agent"
          title="Agent Report"
        />
        <Col xl={12} className="mb-lg-3">
          <Card>
            <CardBody>
              <Row className=" justify-content-between">
                {/* Left side: Excel & PDF buttons */}
                <Col xl={2}>
                  <div className="d-flex gap-2">
                    <Button
                      color="primary"
                      onClick={exportPDF}
                      className="d-flex justify-content-center  p-0"
                      style={{ width: "30px", height: "30px" }}
                      title="PDF"
                    >
                      <FaFilePdf size={16} />
                    </Button>
                    <Button
                      color="secondary"
                      onClick={exportExcel}
                      className="d-flex justify-content-center  p-0"
                      style={{ width: "30px", height: "30px" }}
                      title="EXCEL"
                    >
                      <FaFileExcel size={16} />
                    </Button>
                  </div>
                </Col>

                {/* Right side: Search box and buttons */}
                <Col xl={3}>
                  <div className="mb-3 d-flex align-items-center">
                    <Input
                      type="text"
                      placeholder="Search..."
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                    />
                  </div>
                </Col>
              </Row>

              <div className="table-responsive custom-scrollbar">
                <DataTable
                  data={filteredItems}
                  columns={agentTableColumns}
                  highlightOnHover
                  striped
                  customStyles={customStyles}
                  pagination
                  paginationServer
                  paginationPerPage={perPage}
                  paginationTotalRows={totalRows}
                  onChangePage={handlePageChange}
                  onChangeRowsPerPage={handlePerRowsChange}
                  paginationDefaultPage={currentPage}
                  sortServer
                  onSort={handleSort}
                  responsive
                  progressPending={loading}
                  progressComponent={
                    <div className="p-5">
                      <Spinner color="primary" />
                    </div>
                  }
                />
              </div>
            </CardBody>
          </Card>
        </Col>
        <ToastContainer />
      </div>
    </>
  );
}

// // // ////with search reset button  dont delete typing search without flicker Dont delete
// import React, { useState, useEffect } from "react";
// import { Card, CardBody, Col, Button, Spinner, Input, Row } from "reactstrap";
// import DataTable from "react-data-table-component";
// import { AgentTitle, ViewAgentTitle } from "../../../Utils/Constants";
// import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
// import axios from "axios";
// import { baseUrl } from "../../../Utils/FileUpload";
// import FilterComponent from "../../../Components/Tables/DataTables/Common/FilterComponent";
// import { ToastContainer, toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import { useNavigate } from "react-router-dom";
// import jsPDF from "jspdf";
// import autoTable from "jspdf-autotable";
// import * as XLSX from "xlsx";
// import { saveAs } from "file-saver";
// import { FaFilePdf, FaFileExcel } from "react-icons/fa";
// interface Agent {
//   id: string;
//   agent_name: string;
//   phone: number;
//   total_amount: number;
//   total_call_duration: string;
//   currency: string;
//   total_unique_customers: number;
//   total_calls: number;
//   email: string;
// }

// export default function AgentReport() {
//   const [filterText, setFilterText] = useState("");
//   const [filteredItems, setFilteredItems] = useState<Agent[]>([]);
//   const token = localStorage.getItem("token");
//   const navigate = useNavigate();
//   const [totalRows, setTotalRows] = useState<number>(0);
//   const [perPage, setPerPage] = useState<number>(10);
//   const [currentPage, setCurrentPage] = useState<number>(1);
//   const [reportMonth, setReportMonth] = useState<string>("");
//   const [loading, setLoading] = useState(true);
//   const fetchData = async (page = 1, perPage = 10) => {
//     try {
//       setLoading(true);
//       const response = await axios.get(`${baseUrl}agent/get_all_report`, {
//         params: {
//           page,
//           per_page: perPage,
//         },
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       });

//       const data: Agent[] = response.data.data;
//       console.log(response.data, "data");
//       setFilteredItems(data);
//       setTotalRows(response.data.total);
//       setCurrentPage(response.data.current_page);
//       setReportMonth(response.data.report_month);
//     } catch (error) {
//       console.error("Error fetching data:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     if (filterText.trim() === "") {
//       fetchData(currentPage, perPage); // Go back to original page
//       setCurrentPage(currentPage); // Sync UI page
//     }
//   }, [filterText]);
//   useEffect(() => {
//     fetchData(currentPage, perPage);
//   }, [currentPage, perPage]);

//   const handlePageChange = (page: number) => {
//     setCurrentPage(page);
//     fetchData(page, perPage); // pass search even when paginating after search
//   };

//   const handlePerRowsChange = async (newPerPage: number, page: number) => {
//     setPerPage(newPerPage);
//     setCurrentPage(1);
//     fetchData(1, newPerPage);
//   };

//   const fetchAllAgentDataWithoutPagination = async (
//     searchText = ""
//   ): Promise<Agent[]> => {
//     try {
//       const response = await axios.get(
//         `${baseUrl}agent/get_all_no_pagination`,
//         {
//           params: {
//             search: searchText.trim() !== "" ? searchText : undefined,
//           },
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//         }
//       );
//       console.log(response.data.data, "search");

//       return response.data.data || [];
//     } catch (error) {
//       console.error("Error fetching all agent data without pagination:", error);
//       return []; // Return empty array on error
//     }
//   };

// const handleSearchClick = async () => {
//   // Always reset to page 1 when searching
//   setCurrentPage(1);

//   if (filterText.trim() === "") {
//     // If search is empty, fetch normal paginated data
//     fetchData(1, perPage);
//     return;
//   }

//   // Perform search immediately
//   const data = await fetchAllAgentDataWithoutPagination(filterText);
//   setFilteredItems(data);
//   setTotalRows(data.length);
// };

// // Modify the useEffect to handle page changes only when not searching
// useEffect(() => {
//   if (filterText.trim() === "") {
//     fetchData(currentPage, perPage);
//   }
// }, [currentPage, perPage]);

// // Add this useEffect to trigger search when filterText changes
// useEffect(() => {
//   const timer = setTimeout(() => {
//     if (filterText.trim() !== "") {
//       handleSearchClick();
//     }
//   }, 500); // Small debounce delay

//   return () => clearTimeout(timer);
// }, [filterText]);
//   const handleReset = () => {
//     setFilterText("");
//     setCurrentPage(currentPage); // restore to the previous page
//     fetchData(currentPage, perPage); // fetch without search
//   };

//   const formatAmount = (value: number | string | undefined) => {
//     const num = Number(value) || 0;
//     return Number.isInteger(num) ? num : num.toFixed(2);
//   };

//   const agentTableColumns = [
//     {
//       name: "S.No",
//       cell: (row: Agent, index: number) => (
//         <>{(currentPage - 1) * perPage + index + 1}</>
//       ),
//       width: "10%",
//     },
//     {
//       name: "Agent Name",
//       selector: (row: Agent) => row.agent_name || "N/A",
//       width: "18%",
//     },
//     {
//       name: "Contact Number",
//       selector: (row: Agent) => row.phone || "N/A",
//       width: "18%",
//     },
//     {
//       name: "Total Call Duration",
//       selector: (row: Agent) => `${row.total_call_duration}`,
//       width: "18%",
//     },
//     {
//       name: "Total Customer",
//       selector: (row: Agent) =>
//         `${row.total_unique_customers}( ${row.total_calls})`,
//       width: "18%",
//     },
//     {
//       name: "Total Amount",
//       selector: (row: Agent) =>
//         `${row.currency}${formatAmount(row.total_amount)}`,
//       width: "18%",
//     },
//   ];

//   const customStyles = {
//     headCells: {
//       style: {
//         fontSize: "16px",
//         fontWeight: "600",
//         color: "#308e87",
//         backgroundColor: "#f8f9fa",
//         borderBottom: "2px solid #308e87",
//       },
//     },
//     rows: {
//       style: {
//         borderBottom: "1px solid #dee2e6",
//       },
//     },
//     table: {
//       style: {
//         border: "1px solid #dee2e6",
//         borderRadius: "8px",
//       },
//     },
//   };

//   if (loading) {
//     return (
//       <div
//         style={{
//           display: "flex",
//           justifyContent: "center",
//           alignItems: "center",
//           height: "100vh",
//         }}
//       >
//         <Spinner color="primary" />
//       </div>
//     );
//   }

//   const exportPDF = async () => {
//     const allAgentData = await fetchAllAgentDataWithoutPagination(filterText);

//     if (!allAgentData.length) {
//       toast.warn("No agent data available for export.", {
//         position: "top-right",
//         autoClose: 1000,
//         hideProgressBar: false,
//         closeOnClick: true,
//       });
//       return;
//     }

//     const doc = new jsPDF();

//     const agentTableColumns = [
//       "S.No",
//       "Agent Name",
//       "Total Call Duration",
//       "Total Customer (Calls)",
//       "Total Amount",
//     ];

//     const tableRows = allAgentData.map((row, index) => [
//       index + 1,
//       row.agent_name || "N/A",
//       row.total_call_duration || "0",
//       `${row.total_unique_customers || 0} (${row.total_calls || 0} call${
//         row.total_calls === 1 ? "" : "s"
//       })`,
//       `${formatAmount(row.total_amount) || "0"}`,
//     ]);

//     autoTable(doc, {
//       head: [agentTableColumns],
//       body: tableRows,
//       startY: 20,
//     });

//     doc.save("Agent_Report.pdf");
//   };

//   const exportExcel = async () => {
//     const allAgentData = await fetchAllAgentDataWithoutPagination(filterText);

//     if (!allAgentData.length) {
//       toast.warn("No agent data available for export.", {
//         position: "top-right",
//         autoClose: 1000,
//         hideProgressBar: false,
//         closeOnClick: true,
//       });
//       return;
//     }

//     const tableData = allAgentData.map((row, index) => ({
//       "S.No": index + 1,
//       "Agent Name": row.agent_name || "N/A",
//       "Total Call Duration": row.total_call_duration || "0",
//       "Total Customer (Calls)": `${row.total_unique_customers || 0} (${
//         row.total_calls || 0
//       } call${row.total_calls === 1 ? "" : "s"})`,
//       "Total Amount": `${row.currency || ""}${
//         formatAmount(row.total_amount) || "0"
//       }`,
//     }));

//     const worksheet = XLSX.utils.json_to_sheet(tableData);
//     const workbook = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(workbook, worksheet, "Call_Report");

//     const excelBuffer = XLSX.write(workbook, {
//       bookType: "xlsx",
//       type: "array",
//     });

//     const blob = new Blob([excelBuffer], {
//       type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
//     });
//     saveAs(blob, "Agent_Report.xlsx");
//   };

//   return (
//     <>
//       <div className="page-body">
//         <Breadcrumbs
//           pageTitle={`Agent Report${reportMonth ? ` (${reportMonth})` : ""}`}
//           parent="Agent"
//           title="Agent Report"
//         />
//         <Col xl={12} className="mb-lg-3">
//           <Card>
//             <CardBody>
//               <Row className=" justify-content-between">
//                 {/* Left side: Excel & PDF buttons */}
//                 <Col xl={2}>
//                   <div className="d-flex gap-2">
//                     <Button
//                       color="primary"
//                       onClick={exportPDF}
//                       className="d-flex justify-content-center  p-0"
//                       style={{ width: "30px", height: "30px" }}
//                     >
//                       <FaFilePdf size={16} />
//                     </Button>
//                     <Button
//                       color="secondary"
//                       onClick={exportExcel}
//                       className="d-flex justify-content-center  p-0"
//                       style={{ width: "30px", height: "30px" }}
//                     >
//                       <FaFileExcel size={16} />
//                     </Button>
//                   </div>
//                 </Col>

//                 {/* Right side: Search box and buttons */}
//                 <Col xl={4}>
//                   <div className="mb-3 d-flex align-items-center">
//                     <Input
//                       type="text"
//                       placeholder="Search"
//                       value={filterText}
//                       onChange={(e) => setFilterText(e.target.value)}
//                       className="me-2"
//                     />
//                     <Button
//                       color="primary"
//                       onClick={handleSearchClick}
//                       className="me-2"
//                       title="Search"
//                     >
//                       <i className="fa fa-search" />
//                     </Button>
//                     <Button
//                       color="danger"
//                       onClick={() => handleReset()}
//                       title="Reset Search"
//                     >
//                       <i className="fa fa-refresh" />
//                     </Button>
//                   </div>
//                 </Col>
//               </Row>

//               <div className="table-responsive custom-scrollbar">
//                 <DataTable
//                   data={filteredItems}
//                   columns={agentTableColumns}
//                   highlightOnHover
//                   striped
//                   customStyles={customStyles}
//                   pagination
//                   paginationServer
//                   paginationPerPage={perPage}
//                   paginationTotalRows={totalRows}
//                   onChangePage={handlePageChange}
//                   onChangeRowsPerPage={handlePerRowsChange}
//                   paginationDefaultPage={currentPage}
//                   responsive
//                 />
//               </div>
//             </CardBody>
//           </Card>
//         </Col>
//         <ToastContainer />
//       </div>
//     </>
//   );
// }
